/**
 * NLU review loop, operator end (spec 2026-10-01 §7–§8).
 *
 * The bot's review list (messages it did not understand — comments, and DMs when
 * capture is on) grouped by meaning, most-repeated first. One decision per cluster:
 *
 *   - **Add to intent** (existing or new) — every member is saved as a gold label, then
 *     only the members in the *train* split become intent phrases. Test-split members are
 *     labelled but never added: the gold set measures the index, it must not be in it.
 *   - **None** — understood, but no intent applies (the caller falls back to RAG/LLM).
 *   - **Noise** — junk.
 *
 * Order per decision: gold labels → phrases → remove the rows. Each step is idempotent,
 * so if one fails the rows stay and repeating the decision is safe.
 */
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { hubClient, type GoldLabelKind, type ReviewCluster } from '../../services/hubClient';
import { hubService, type Intent } from '../../services/hub.service';
import { MissShape } from './UnrecognizedCommentsTab';
import { canCreateIntent, CREATE_HINT } from '../../lib/intentView';

type Mode = 'existing' | 'new';

export interface ReviewClustersTabProps {
  system: string;
  botId: string;
}

export function ReviewClustersTab({ system, botId }: ReviewClustersTabProps) {
  const { getToken } = useAuth();
  const [clusters, setClusters] = useState<ReviewCluster[]>([]);
  const [intents, setIntents] = useState<Intent[]>([]);
  const [chosen, setChosen] = useState<Record<string, string>>({});
  const [mode, setMode] = useState<Record<string, Mode>>({});
  const [newIntent, setNewIntent] = useState<Record<string, { code: string; name: string }>>({});
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cs, ints] = await Promise.all([
        hubClient.listReviewClusters({ system, botId }),
        hubService.listIntentsByBot(botId, system, getToken()).catch(() => [] as Intent[]),
      ]);
      setClusters(cs);
      setIntents(ints);
      // Preselect the gate's own top candidate (same rule as the per-row tab).
      const codeToIntent = new Map(ints.map((i) => [i.intentCode, i.id]));
      setChosen((prev) => {
        const next = { ...prev };
        for (const c of cs) {
          if (next[c.leaderId]) continue;
          const hit = c.candidateCodes.find((code) => codeToIntent.has(code));
          if (hit) next[c.leaderId] = codeToIntent.get(hit)!;
        }
        return next;
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load clusters');
    } finally {
      setLoading(false);
    }
  }, [system, botId, getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  async function decide(c: ReviewCluster, labelKind: GoldLabelKind) {
    setBusyId(c.leaderId);
    setError(null);
    setNotice(null);
    let step = 'gold labels';
    try {
      let intentId: string | undefined;
      let intentCode: string | undefined;
      if (labelKind === 'intent') {
        if ((mode[c.leaderId] ?? 'existing') === 'new') {
          const draft = newIntent[c.leaderId];
          if (!draft?.code?.trim() || !draft?.name?.trim()) {
            setError('New intent needs a code and a name');
            return;
          }
          intentCode = draft.code.trim();
          // Create is an upsert on (system, botId, intentCode): for a code that already exists
          // it would rename that intent. Reuse it instead.
          const existing = intents.find((i) => i.intentCode === intentCode);
          if (existing) {
            intentId = existing.id;
          } else {
            step = 'create intent';
            const created = await hubService.createIntent(
              { system, botId, intentCode, name: draft.name.trim() },
              getToken(),
            );
            intentId = created.id;
          }
        } else {
          intentId = chosen[c.leaderId];
          intentCode = intents.find((i) => i.id === intentId)?.intentCode;
          if (!intentId || !intentCode) {
            setError('Pick an intent first');
            return;
          }
        }
      }

      step = 'gold labels';
      const { splits } = await hubClient.upsertGoldLabels({
        system,
        botId,
        items: c.members.map((m) => ({
          text: m.text,
          labelKind,
          intentCode,
          surface: m.surface === 'comment' || m.surface === 'dm' ? m.surface : undefined,
        })),
      });

      let added = 0;
      let heldOut = 0;
      if (labelKind === 'intent' && intentId) {
        // Only an explicit 'train' becomes a phrase; anything unknown is held out (never leak a test text).
        const train = c.members.filter((m) => splits[m.text] === 'train').map((m) => m.text);
        heldOut = c.members.length - train.length;
        if (train.length > 0) {
          step = 'add phrases';
          await hubService.createBulkIntentTexts(intentId, train, getToken(), 'review');
          added = train.length;
        }
      }

      step = 'remove rows';
      for (const m of c.members) {
        // 404 = already removed (a retry, or another operator): that is the goal state.
        await hubClient.deleteUnrecognizedComment(m.id).catch((e: unknown) => {
          if (!(e instanceof Error && e.message.startsWith('404'))) throw e;
        });
      }

      setNotice(
        labelKind === 'intent'
          ? `${added} added as phrases, ${heldOut} kept for testing`
          : `${c.members.length} labelled as ${labelKind}`,
      );
      await load();
    } catch (e) {
      setError(`Failed at "${step}" — rows left in place, repeating is safe. ${e instanceof Error ? e.message : ''}`);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm">
        <button className="rounded border px-2 py-1" onClick={() => void load()} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
        <span className="text-gray-500">{clusters.length} clusters</span>
      </div>

      {error && <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
      {notice && <div className="rounded bg-green-50 px-3 py-2 text-sm text-green-700">{notice}</div>}
      {!loading && clusters.length === 0 && !error && (
        <div className="text-sm text-gray-500">Nothing to review for this bot.</div>
      )}

      <ul className="divide-y rounded border">
        {clusters.map((c) => {
          const busy = busyId === c.leaderId;
          const m = mode[c.leaderId] ?? 'existing';
          const surfaces = Array.from(new Set(c.members.map((x) => x.surface).filter(Boolean)));
          return (
            <li key={c.leaderId} className="space-y-2 p-3">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  className="text-left font-medium"
                  onClick={() => setOpen((o) => ({ ...o, [c.leaderId]: !o[c.leaderId] }))}
                >
                  {c.text}
                </button>
                <span className="text-xs text-gray-500">×{c.totalSeen}</span>
                {c.size > 1 && <span className="text-xs text-gray-500">{c.size} phrasings</span>}
                {surfaces.map((s) => (
                  <span key={s} className="rounded bg-blue-50 px-1.5 py-0.5 text-[11px] text-blue-700">{s}</span>
                ))}
                <MissShape row={c} />
              </div>

              {open[c.leaderId] && (
                <ul className="ml-4 list-disc text-sm text-gray-700">
                  {c.members.map((x) => (
                    <li key={x.id}>
                      {x.text} <span className="text-xs text-gray-400">×{x.seenCount}</span>
                    </li>
                  ))}
                </ul>
              )}

              <div className="flex flex-wrap items-center gap-2 text-sm">
                <select
                  className="rounded border px-2 py-1"
                  value={m}
                  onChange={(e) => setMode((s) => ({ ...s, [c.leaderId]: e.target.value as Mode }))}
                >
                  <option value="existing">Existing intent</option>
                  <option value="new" disabled={!canCreateIntent(system)} title={canCreateIntent(system) ? undefined : CREATE_HINT}>
                    New intent{canCreateIntent(system) ? '' : ' (create it in chatbot)'}
                  </option>
                </select>
                {m === 'existing' ? (
                  <select
                    className="rounded border px-2 py-1"
                    value={chosen[c.leaderId] ?? ''}
                    onChange={(e) => setChosen((s) => ({ ...s, [c.leaderId]: e.target.value }))}
                  >
                    <option value="">— pick intent —</option>
                    {intents.map((i) => (
                      <option key={i.id} value={i.id}>{i.intentCode}</option>
                    ))}
                  </select>
                ) : (
                  <>
                    <input
                      className="rounded border px-2 py-1"
                      placeholder="intent code"
                      value={newIntent[c.leaderId]?.code ?? ''}
                      onChange={(e) =>
                        setNewIntent((s) => ({ ...s, [c.leaderId]: { code: e.target.value, name: s[c.leaderId]?.name ?? '' } }))
                      }
                    />
                    <input
                      className="rounded border px-2 py-1"
                      placeholder="name"
                      value={newIntent[c.leaderId]?.name ?? ''}
                      onChange={(e) =>
                        setNewIntent((s) => ({ ...s, [c.leaderId]: { code: s[c.leaderId]?.code ?? '', name: e.target.value } }))
                      }
                    />
                  </>
                )}
                <button
                  className="rounded bg-blue-600 px-2 py-1 text-white disabled:opacity-50"
                  disabled={busy}
                  onClick={() => void decide(c, 'intent')}
                >
                  Add to intent
                </button>
                <button className="rounded border px-2 py-1" disabled={busy} onClick={() => void decide(c, 'none')}>
                  None
                </button>
                <button className="rounded border px-2 py-1" disabled={busy} onClick={() => void decide(c, 'noise')}>
                  Noise
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
